import { createContext } from 'react';

import type {
  InventoryAction,
  InventoryProvider,
  InventoryTree,
  InventoryType,
} from '@scaleaq/sitecontrol-lib';
import type { UseQueryResult } from '@tanstack/react-query';

import { useRequiredContext } from '@/hooks';

export type InventoryDataContextValue = {
  /** Roots assembled in `InventoryDataProvider` from the flat `useGetAllInventory` list. */
  roots: InventoryTree[] | undefined;
  /** Flat id → node map for the forest under {@link roots} (active-site edge tree). */
  inventoryById: ReadonlyMap<string, InventoryTree>;
  /**
   * Flat `id → display name` map from the `getNames` endpoint. Mode-aware via the cloud API key:
   * spans every accessible site in portable mode and the local site on edge clusters. Lets any
   * consumer resolve an inventory's name by id regardless of its type or site (e.g. a camera's
   * parent pen/barge/site), without loading the full cross-site tree.
   */
  inventoryNameById: ReadonlyMap<string, string>;
  isInventoryNamesLoading: boolean;
  /**
   * Children of the first root node. Matches the legacy event `InventoryProvider` shape
   * (`data[0].inventories`).
   */
  firstRootInventories: InventoryTree[];
  /**
   * `providers` from each top-level root, flattened (same as `roots?.map(r => r.providers).flat()`
   * with an empty fallback). Does not walk child inventories.
   */
  rootProviders: InventoryProvider[];
  /**
   * `actions` from each top-level root, flattened (same as `roots?.flatMap(r => r.actions) ?? []`).
   * Does not walk child inventories.
   */
  rootActions: InventoryAction[];
  /** Non-deleted types on this site (`GET /api/v1/distincttypes`). */
  distinctInventoryTypes: InventoryType[] | undefined;
  isDistinctTypesLoading: boolean;
  isDistinctTypesError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isFetched: boolean;
  error: UseQueryResult<InventoryTree[]>['error'];
};

export const InventoryDataContext = createContext<InventoryDataContextValue | undefined>(undefined);

export const useInventoryDataCtx = () =>
  useRequiredContext({
    context: InventoryDataContext,
    hookName: 'useInventoryDataCtx',
    providerName: 'InventoryDataProvider',
  });
